import type { T_ChartData } from "@lattice/core-lib/types/chart";
import {
  z_ChatV8StockWidgetColumnarDataResponse,
  type T_ChatV8StockWidgetBar,
  type T_ChatV8StockWidgetColumnarInterval,
  type T_ChatV8StockWidgetData,
  type T_ChatV8StockWidgetDataResponse,
  type T_ChatV8StockWidgetInterval,
} from "@lattice/core-lib/types/chat-v8";

const MAX_INTRADAY_RENDERED_PRICE_POINTS = 1_000;
const MAX_LONG_RANGE_SOURCE_POINTS = 2_500;
const MAX_LONG_RANGE_RENDERED_PRICE_POINTS = 2_000;

/** Compact stock-card market cap (RAI-1082): one decimal at the natural
 * magnitude, never a raw multi-digit dollar integer. */
export function formatStockWidgetMarketCap(
  value: number | null,
): string | null {
  if (value === null || !Number.isFinite(value)) return null;
  const absolute = Math.abs(value);
  const scale =
    absolute >= 1_000_000_000_000
      ? { divisor: 1_000_000_000_000, suffix: "T" }
      : absolute >= 1_000_000_000
        ? { divisor: 1_000_000_000, suffix: "B" }
        : absolute >= 1_000_000
          ? { divisor: 1_000_000, suffix: "M" }
          : absolute >= 1_000
            ? { divisor: 1_000, suffix: "K" }
            : { divisor: 1, suffix: "" };
  const formatted = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 1,
  }).format(value / scale.divisor);
  return `$${formatted}${scale.suffix}`;
}

/** FMP dividend yield is a decimal ratio (0.012 = 1.2%). A zero yield is not
 * useful stock-card information and must be omitted rather than rendered as
 * "Dividend 0%" (RAI-1082). */
export function formatStockWidgetDividendYield(
  value: number | null,
): string | null {
  if (value === null || !Number.isFinite(value) || value <= 0) return null;
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
    style: "percent",
  }).format(value);
}

/** Shared optional metric formatting keeps mobile/web null behavior aligned. */
export function formatStockWidgetRatio(value: number | null): string | null {
  if (value === null || !Number.isFinite(value)) return null;
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  }).format(value);
}

export function parseChatV8StockWidgetSnapshotData(
  value: unknown,
): T_ChatV8StockWidgetDataResponse | null {
  const columnar = z_ChatV8StockWidgetColumnarDataResponse.safeParse(value);
  if (!columnar.success) return null;

  return {
    ...columnar.data,
    data: columnar.data.data
      ? {
          ...columnar.data.data,
          intervals: {
            "1D": inflateColumnarInterval(columnar.data.data.intervals["1D"]),
            "1W": inflateColumnarInterval(columnar.data.data.intervals["1W"]),
            "1M": inflateColumnarInterval(columnar.data.data.intervals["1M"]),
            "6M": inflateColumnarInterval(columnar.data.data.intervals["6M"]),
            "1Y": inflateColumnarInterval(columnar.data.data.intervals["1Y"]),
            "5Y": inflateColumnarInterval(columnar.data.data.intervals["5Y"]),
            MAX: inflateColumnarInterval(columnar.data.data.intervals.MAX),
          },
        }
      : null,
  };
}

function inflateColumnarInterval(
  interval: T_ChatV8StockWidgetColumnarInterval,
): T_ChatV8StockWidgetBar[] {
  const bars: T_ChatV8StockWidgetBar[] = [];
  const count = Math.min(interval.t.length, interval.c.length);

  for (let index = 0; index < count; index += 1) {
    const timestamp = interval.t[index];
    const close = interval.c[index];
    if (
      typeof timestamp !== "number" ||
      typeof close !== "number" ||
      !Number.isFinite(close)
    ) {
      continue;
    }

    bars.push({
      t: new Date(timestamp).toISOString(),
      o: close,
      h: close,
      l: close,
      c: close,
      v: 0,
    });
  }

  return bars;
}

export function getChatV8StockWidgetIntervalBars(args: {
  data: T_ChatV8StockWidgetData;
  interval: T_ChatV8StockWidgetInterval;
}): T_ChatV8StockWidgetBar[] {
  return args.data.intervals[args.interval];
}

export function buildChatV8StockPriceChartJson(args: {
  bars: T_ChatV8StockWidgetBar[];
  interval: T_ChatV8StockWidgetInterval;
}): string | null {
  const bars = selectRenderedPricePoints({
    interval: args.interval,
    items: args.bars,
  });
  if (bars.length < 2) return null;

  const chartData: T_ChartData = {
    type: "line",
    // The enclosing stock card already owns company identity and the interval
    // selector owns range. Repeating both here produced "AXON" and price
    // headers multiple times in one answer (RAI-1083). A price chart is the
    // default investor view, so the axis and currency format carry the
    // meaning without a "Price" heading either (RAI-1096) — the field is
    // omitted rather than blank so renderers skip the heading slot entirely.
    valueFormat: "currency",
    data: bars.map((bar) => ({
      label: formatStockWidgetBarLabel({
        interval: args.interval,
        timestamp: bar.t,
      }),
      detailLabel: formatStockWidgetBarDetailLabel({
        interval: args.interval,
        timestamp: bar.t,
      }),
      value: bar.c,
    })),
  };

  return JSON.stringify(chartData);
}

export function buildChatV8StockWidgetChartJson(args: {
  data: T_ChatV8StockWidgetData;
  interval: T_ChatV8StockWidgetInterval;
}): string | null {
  return buildChatV8StockPriceChartJson({
    bars: getChatV8StockWidgetIntervalBars(args),
    interval: args.interval,
  });
}

function selectRenderedPricePoints(args: {
  interval: T_ChatV8StockWidgetInterval;
  items: T_ChatV8StockWidgetBar[];
}): T_ChatV8StockWidgetBar[] {
  if (
    (args.interval === "1D" ||
      args.interval === "1W" ||
      args.interval === "1M" ||
      args.interval === "6M") &&
    args.items.length > MAX_INTRADAY_RENDERED_PRICE_POINTS
  ) {
    return selectLttbPoints({
      items: args.items,
      maxItems: MAX_INTRADAY_RENDERED_PRICE_POINTS,
    });
  }

  if (
    args.interval === "MAX" &&
    args.items.length > MAX_LONG_RANGE_SOURCE_POINTS
  ) {
    return selectLttbPoints({
      items: args.items,
      maxItems: MAX_LONG_RANGE_RENDERED_PRICE_POINTS,
    });
  }

  return args.items;
}

function selectLttbPoints<T_Item extends { c: number }>(args: {
  items: T_Item[];
  maxItems: number;
}): T_Item[] {
  if (args.items.length <= args.maxItems) return args.items;
  if (args.maxItems <= 1) return [];

  const bucketSize = (args.items.length - 2) / (args.maxItems - 2);
  const result: T_Item[] = [args.items[0]].filter(
    (item): item is T_Item => item !== undefined,
  );
  let selectedIndex = 0;

  for (let bucketIndex = 0; bucketIndex < args.maxItems - 2; bucketIndex += 1) {
    const rangeStart = Math.floor((bucketIndex + 0) * bucketSize) + 1;
    const rangeEnd = Math.floor((bucketIndex + 1) * bucketSize) + 1;
    const nextRangeStart = Math.floor((bucketIndex + 1) * bucketSize) + 1;
    const nextRangeEnd = Math.floor((bucketIndex + 2) * bucketSize) + 1;
    const average = getAveragePoint({
      end: Math.min(nextRangeEnd, args.items.length),
      items: args.items,
      start: Math.min(nextRangeStart, args.items.length),
    });
    const anchor = args.items[selectedIndex];
    if (!anchor) {
      continue;
    }

    let bestArea = -1;
    let bestIndex = rangeStart;
    for (
      let sourceIndex = rangeStart;
      sourceIndex < Math.min(rangeEnd, args.items.length - 1);
      sourceIndex += 1
    ) {
      const candidate = args.items[sourceIndex];
      if (!candidate) continue;
      const area = Math.abs(
        (selectedIndex - average.x) * (candidate.c - anchor.c) -
          (selectedIndex - sourceIndex) * (average.y - anchor.c),
      );
      if (area > bestArea) {
        bestArea = area;
        bestIndex = sourceIndex;
      }
    }

    const bestItem = args.items[bestIndex];
    if (bestItem) {
      result.push(bestItem);
      selectedIndex = bestIndex;
    }
  }

  const last = args.items[args.items.length - 1];
  if (last) result.push(last);
  return result;
}

function getAveragePoint<T_Item extends { c: number }>(args: {
  end: number;
  items: T_Item[];
  start: number;
}) {
  let count = 0;
  let x = 0;
  let y = 0;

  for (let index = args.start; index < args.end; index += 1) {
    const item = args.items[index];
    if (!item) continue;
    count += 1;
    x += index;
    y += item.c;
  }

  if (count === 0) {
    return { x: args.start, y: 0 };
  }

  return { x: x / count, y: y / count };
}

/**
 * Intl.DateTimeFormat construction is expensive (~40µs in Bun, far worse on
 * Hermes). These run per bar — up to 1,000 rendered points × 2 labels per
 * interval switch — so formatters are cached at module scope. Lazy so module
 * import stays cheap.
 */
const stockWidgetFormatterCache = new Map<string, Intl.DateTimeFormat>();

function getStockWidgetFormatter(
  key: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  let formatter = stockWidgetFormatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", options);
    stockWidgetFormatterCache.set(key, formatter);
  }
  return formatter;
}

function formatStockWidgetBarLabel(args: {
  interval: T_ChatV8StockWidgetInterval;
  timestamp: string;
}): string {
  const date = new Date(args.timestamp);
  if (Number.isNaN(date.getTime())) return args.timestamp;

  if (args.interval === "1D") {
    return normalizeStockWidgetAxisLabel(
      getStockWidgetFormatter("time", {
        hour: "numeric",
        minute: "2-digit",
      }).format(date),
    );
  }

  if (args.interval === "1W" || args.interval === "1M") {
    return getStockWidgetFormatter("day-month", {
      day: "numeric",
      month: "short",
    }).format(date);
  }

  return getStockWidgetFormatter("month-year", {
    month: "short",
    year: "2-digit",
  }).format(date);
}

/**
 * Long-form point label for scrub/header readouts ("Jul 24, 10:20 AM" on
 * intraday ranges, "Jul 24, 2026" on longer ones). The short axis `label`
 * stays interval-appropriate; this one always carries the full date.
 */
function formatStockWidgetBarDetailLabel(args: {
  interval: T_ChatV8StockWidgetInterval;
  timestamp: string;
}): string {
  const date = new Date(args.timestamp);
  if (Number.isNaN(date.getTime())) return args.timestamp;

  if (args.interval === "1D" || args.interval === "1W") {
    return normalizeStockWidgetAxisLabel(
      getStockWidgetFormatter("detail-datetime", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }).format(date),
    );
  }

  return getStockWidgetFormatter("detail-date", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

/**
 * Hermes can emit a narrow no-break space before AM/PM. The Android Skia axis
 * font does not contain that glyph, so normalize locale whitespace before the
 * label crosses the chart renderer boundary.
 */
export function normalizeStockWidgetAxisLabel(value: string): string {
  return value.replace(/\s+/g, " ");
}
